
import Vue from 'vue'
import Component from 'vue-class-component'
import {Prop} from "vue-property-decorator";
import {namespace} from "vuex-class";
import {IScreensaver, IScreensaverElement} from "@/model/Screensaver/screensaverInterfaces";
import {IErrors} from "@/model/IErrors";
import {EPermissionActions} from "@/model/EPermissionActions";
import {UrlHelper} from "@/helpers/UrlHelper";
import {IOnOffHandlers} from "bonomo/dist";
import TranslatableTabs from "@/components/shared/TranslatableTabs.vue";
import ImageUploader from "@/components/shared/ImageUploader.vue";
import ServiceProviderSearch from "@/components/serviceProvider/widget/ServiceProviderSearch.vue";
import ScreensaverLogs from "@/components/user/logs/ScreensaverLogs.vue";

const screensaverModule = namespace("screensaverModule");

interface ScreensaverForm {
  name: string;
  active: boolean;
  elements: Record<string, any>;
}

@Component({
  name: 'ScreensaverInfo',
  components: {
    TranslatableTabs,
    ImageUploader,
    ServiceProviderSearch,
    ScreensaverLogs,
  }
})
export default class ScreensaverInfo extends Vue {

  @Prop()
  protected screensaverId!: number;

  @Prop()
  protected type!: string;

  @screensaverModule.Getter('screensaver')
  protected screensaver!: IScreensaver | null;

  @screensaverModule.Action('fetchScreensaver')
  protected fetchScreensaver!: (screensaverId: number) => any;

  @screensaverModule.Action('fetchScreensaverTemplate')
  protected fetchScreensaverTemplate!: (type: string) => any;

  @screensaverModule.Action('createScreensaver')
  protected createScreensaverAction!: (data: any) => any;

  @screensaverModule.Action('updateScreensaver')
  protected updateScreensaverAction!: (data: { screensaverId: number, data: any }) => any;

  @screensaverModule.Getter('errors')
  protected errors!: IErrors;

  protected nameRequiredRule = [(v: string) => !!v || this.$t('screen_savers.edit.errors.nameRequired')];

  protected languages: string[] = ['es-ES', 'ca-ES', 'en-GB', 'fr-FR', 'pt-PT', 'dk-DK', 'de-DE'];
  protected defaultLanguage: string = 'es-ES';
  protected requiredLanguages: string[] = ['es-ES', 'en-GB'];

  protected loading: boolean = true;
  protected saving: boolean = false;
  protected valid: boolean = false;

  protected form: ScreensaverForm = {
    name: '',
    active: false,
    elements: {},
  };
  protected imageValues: Record<string, string | null> = {};
  private pendingImageFiles: Record<string, File> = {};

  private subscribes: IOnOffHandlers = {};

  protected get canEdit(): boolean {
    return this.$canAction(EPermissionActions.ACTION_SCREENSAVERS_UPDATE);
  }

  protected get isCreate(): boolean {
    return !this.screensaverId;
  }

  protected get oldBackofficeUrl(): string {
    return UrlHelper.mayorOldScreensaverEdit(this.screensaverId);
  }

  protected goBack() {
    this.$router.push({name: 'screensavers'});
  }

  protected get plainElements(): IScreensaverElement[] {
    return this.elementsByView(['text', 'text_area'], false);
  }

  protected get translatableElements(): IScreensaverElement[] {
    return this.elementsByView(['text', 'text_area'], true);
  }

  protected get serviceProviderElements(): IScreensaverElement[] {
    return ((this.screensaver && this.screensaver.elements) || []).filter((element) => element.view === 'service_provider');
  }

  protected get imageElements(): IScreensaverElement[] {
    return ((this.screensaver && this.screensaver.elements) || []).filter((element) => element.view === 'image');
  }

  protected get invalidFields(): string[] {
    const fields: string[] = [];
    this.translatableElements
        .filter((element) => element.required)
        .forEach((element) => {
          this.requiredLanguages.forEach((lang) => {
            if (!this.form.elements[element.key][lang]) {
              fields.push(`${element.label} (${lang})`);
            }
          });
        });
    return fields;
  }

  protected isRequiredLang(lang: string): boolean {
    return this.requiredLanguages.indexOf(lang) !== -1;
  }

  protected fieldRequiredRule(label: string) {
    return [(v: string) => !!v || (label + this.$t('screen_savers.edit.errors.fieldRequiredSuffix'))];
  }

  // image and service_provider elements aren't inside <v-form>'s validation (ImageUploader/ServiceProviderSearch
  // don't support :rules), so their "required" check is done here and combined into the save-button gating
  protected get missingRequiredElements(): IScreensaverElement[] {
    const missingImages = this.imageElements.filter((element) => element.required && !this.imageValues[element.key]);
    const missingServiceProviders = this.serviceProviderElements.filter((element) => element.required && !this.form.elements[element.key]);
    return [...missingImages, ...missingServiceProviders];
  }

  protected get missingRequiredLabels(): string[] {
    return [...this.invalidFields, ...this.missingRequiredElements.map((element) => element.label)];
  }

  private elementsByView(views: string[], translatable: boolean): IScreensaverElement[] {
    return ((this.screensaver && this.screensaver.elements) || [])
        .filter((element) => views.indexOf(element.view) !== -1 && !!element.translatable === translatable);
  }

  protected imageEndpoint(elementKey: string): string {
    return 'screensavers/' + this.screensaverId + '/elements/' + elementKey + '/image';
  }

  created() {
    this.subscribes = this.$ons({
      'login.OK': this.loadData,
      'backend.call.OK.screensaverDetailLoaded': this.onScreensaverLoaded,
      'backend.call.KO.screensaverDetailLoaded': this.onSaveError,
    });
    this.loadData();
  }

  beforeDestroy() {
    this.$offs(this.subscribes);
  }

  private loadData() {
    this.loading = true;
    if (this.isCreate) {
      this.fetchScreensaverTemplate(this.type);
    } else {
      this.fetchScreensaver(this.screensaverId);
    }
  }

  protected onScreensaverLoaded() {
    const wasSaving = this.saving;
    const wasCreating = this.isCreate;
    this.loading = false;
    this.saving = false;
    if (!this.screensaver) {
      return;
    }
    const elements: Record<string, any> = {};
    const images: Record<string, string | null> = {};
    (this.screensaver.elements || []).forEach((element) => {
      if (element.view === 'image') {
        images[element.key] = (element.value as string) || null;
      } else if (element.translatable) {
        const existingValue = (element.value as Record<string, string>) || {};
        elements[element.key] = this.languages.reduce((acc: Record<string, string>, lang: string) => {
          acc[lang] = existingValue[lang] || '';
          return acc;
        }, {});
      } else if (element.view === 'service_provider') {
        // backend sends this as a numeric string; ServiceProviderSearch matches ids by strict equality
        elements[element.key] = element.value ? Number(element.value) : null;
      } else {
        elements[element.key] = element.value;
      }
    });
    this.form = {
      name: this.screensaver.name,
      active: !!this.screensaver.active,
      elements,
    };
    // in create mode, keep the locally-picked previews instead of overwriting them with the (empty) server values
    if (!wasCreating || !wasSaving) {
      this.imageValues = images;
    }

    if (wasSaving) {
      if (wasCreating && this.screensaver.id) {
        this.afterCreated(this.screensaver.id);
      } else {
        this.$uiBus.emit('success', this.$t('common.saved'));
      }
    }
  }

  private afterCreated(newScreensaverId: number) {
    const pendingKeys = Object.keys(this.pendingImageFiles);
    const uploads = pendingKeys.map((elementKey) => this.$store.dispatch('imageUploaderModule/uploadImage', {
      endpoint: 'screensavers/' + newScreensaverId + '/elements/' + elementKey + '/image',
      file: this.pendingImageFiles[elementKey],
      translationPrefix: 'screen_savers.edit.errors',
    }));
    Promise.all(uploads).then(() => {
      this.goToEditMode(newScreensaverId);
    }, () => {
      // the screensaver itself was created fine; let the user retry the image from the edit page
      this.goToEditMode(newScreensaverId);
    });
  }

  private goToEditMode(newScreensaverId: number) {
    this.$uiBus.emit('success', this.$t('common.saved'));
    this.$router.replace({name: 'screensaverInfo', params: {screensaverId: String(newScreensaverId)}});
  }

  protected onServiceProviderSelected(elementKey: string, serviceProviderId: number | null) {
    this.form.elements[elementKey] = serviceProviderId;
  }

  protected onImageChanged(elementKey: string, url: string) {
    Vue.set(this.imageValues, elementKey, url);
  }

  protected onLocalImageSelected(elementKey: string, file: File) {
    if (!file) {
      return;
    }
    this.pendingImageFiles[elementKey] = file;
    Vue.set(this.imageValues, elementKey, URL.createObjectURL(file));
  }

  protected onSave() {
    this.saving = true;
    const data: any = {
      name: this.form.name,
      active: this.form.active,
      ...this.form.elements,
    };
    if (this.isCreate) {
      data.type = this.type;
      this.createScreensaverAction(data);
    } else {
      this.updateScreensaverAction({screensaverId: this.screensaverId, data});
    }
  }

  protected onSaveError() {
    this.saving = false;
  }

}
